import { create } from 'zustand';

import type { User } from '@/types/api';

interface AuthStore {
  user: User | null;
  accessToken: string | null;
  remember: boolean;
  setAuth: (payload: { user: User; accessToken: string }, remember?: boolean) => void;
  setAccessToken: (token: string) => void;
  clearAuth: () => void;
}

const STORAGE_KEY = 'pedidoo.auth';

const defaultState = { user: null, accessToken: null, remember: false } as const;

const getInitialState = () => {
  if (typeof window === 'undefined') {
    return defaultState;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return defaultState;
    }

    const parsed = JSON.parse(stored) as { user: User; accessToken: string; remember: boolean };
    return parsed ?? defaultState;
  } catch (error) {
    console.error('Failed to parse auth storage', error);
    return defaultState;
  }
};

const persistState = (state: {
  user: User | null;
  accessToken: string | null;
  remember: boolean;
}) => {
  if (typeof window === 'undefined') return;

  if (!state.remember || !state.user || !state.accessToken) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ user: state.user, accessToken: state.accessToken, remember: state.remember }),
  );
};

export const useAuthStore = create<AuthStore>((set) => ({
  ...getInitialState(),
  setAuth: ({ user, accessToken }, remember = false) => {
    set(() => {
      const nextState = { user, accessToken, remember };
      persistState(nextState);
      return nextState;
    });
  },
  setAccessToken: (token) => {
    set((state) => {
      const nextState = { ...state, accessToken: token };
      persistState(nextState);
      return nextState;
    });
  },
  clearAuth: () => {
    persistState({ user: null, accessToken: null, remember: false });
    set(() => ({ ...defaultState }));
  },
}));
